import { useMemo } from "react";
import { Box, Chip, Stack, Button, Dialog, Portal, Typography, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Add as AddIcon, Check, WhatsApp, AutoAwesome } from "@material-ui/icons";
import { useTranslation } from "react-i18next";

import { formatBudgetShort } from "utils/contacts/budgetFormat";
import type { RealEstateContact } from "../../../../@types/sharedSchema";

type Props = {
  open: boolean;
  contact: Partial<RealEstateContact> | null;
  onAddAnother: () => void;
  onClose: () => void;
};

const TYPE_EMOJI: Record<string, string> = { קונה: "🏡", מוכר: "🏷️", שוכר: "🔑", משכיר: "💰" };
const CONFETTI_COLORS = ["#00AB55", "#3366FF", "#FFC107", "#FF4842", "#7635dc", "#1890FF", "#54D62C"];

const budgetChip = (min: number | undefined, max: number | undefined, rent: boolean): string | null => {
  if (typeof max !== "number" && typeof min !== "number") return null;
  if (typeof min === "number" && typeof max === "number" && min !== max) {
    return `${formatBudgetShort(min, rent)}–${formatBudgetShort(max, rent)}`;
  }
  return formatBudgetShort((max ?? min) as number, rent);
};

export function ContactCreatedCelebration({ open, contact, onAddAnother, onClose }: Props): JSX.Element | null {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const confetti = useMemo(
    () =>
      Array.from({ length: 70 }).map((_, i) => ({
        left: `${(i * 37) % 100}%`,
        bg: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: `${(i % 10) * 0.06}s`,
        duration: `${1.8 + ((i * 13) % 18) / 10}s`,
        round: i % 3 === 0
      })),
    []
  );

  if (!contact) return null;

  const name =
    contact.contactFullName ||
    `${contact.callerFirstName ?? ""} ${contact.callerLastName ?? ""}`.trim() ||
    (translate("contactsPage.newContact.theContact", { defaultValue: "הלקוח" }) as string);

  const phoneDigits = (contact.id || contact.contactId || "").replace(/\D/g, "");

  const chips: string[] = [];
  (contact.customerTypes ?? (contact.customerType ? [contact.customerType] : [])).forEach((tp) => {
    if (tp) chips.push(`${TYPE_EMOJI[tp] ?? ""} ${tp}`.trim());
  });
  const sale = budgetChip(contact.customerBudgetMin, contact.customerBudget, false);
  if (sale) chips.push(sale);
  const rent = budgetChip(contact.customerBudgetRentMin, contact.customerBudgetRent, true);
  if (rent) chips.push(`${rent}/${translate("general.month", { defaultValue: "ח׳" })}`);
  if (contact.roomsMin || contact.roomsMax) {
    const lo = contact.roomsMin ?? contact.roomsMax;
    const hi = contact.roomsMax ?? contact.roomsMin;
    chips.push(lo !== hi ? `${lo}–${hi} ${translate("general.rooms", { defaultValue: "חד׳" })}` : `${lo} ${translate("general.rooms", { defaultValue: "חד׳" })}`);
  }
  const areasCount = contact.interestedInLocations?.length ?? 0;
  if (areasCount) chips.push(`📍 ${areasCount} ${translate("general.areas", { defaultValue: "אזורים" })}`);
  if (contact.crmIsHot) chips.push(`🔥 ${translate("general.hot", { defaultValue: "חם" })}`);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      {/* confetti — rendered in a Portal over the WHOLE window (fixed, above the
          dialog's z-index), not inside the dialog paper where it would be clipped */}
      {open && !reduceMotion && (
        <Portal>
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              overflow: "hidden",
              pointerEvents: "none",
              zIndex: (t) => t.zIndex.modal + 10
            }}
          >
            <style>{`@keyframes nc-fall { 0% { opacity: 1; transform: translateY(-16px) rotate(0); } 100% { opacity: .9; transform: translateY(110vh) rotate(720deg); } }`}</style>
            {confetti.map((c, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  top: -16,
                  left: c.left,
                  width: 9,
                  height: 14,
                  bgcolor: c.bg,
                  borderRadius: c.round ? "50%" : "2px",
                  animation: `nc-fall ${c.duration} linear ${c.delay} forwards`
                }}
              />
            ))}
          </Box>
        </Portal>
      )}

      <Stack alignItems="center" textAlign="center" spacing={2} sx={{ px: 3, py: 4, position: "relative", zIndex: 3 }}>
        <Box
          sx={{
            width: 84,
            height: 84,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            background: "linear-gradient(135deg, #00AB55, #1fc16b)",
            boxShadow: "0 16px 34px -8px rgba(0,171,85,.6)"
          }}
        >
          <Check sx={{ fontSize: 44 }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {translate("contactsPage.newContact.celebration.added", { name, defaultValue: `${name} נוסף/ה! 🎉` })}
        </Typography>

        {chips.length > 0 && (
          <Stack direction="row" flexWrap="wrap" justifyContent="center" sx={{ gap: 0.75 }}>
            {chips.map((c, i) => (
              <Chip key={i} label={c} size="small" sx={{ fontWeight: 700 }} />
            ))}
          </Stack>
        )}

        <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
          {translate("contactsPage.newContact.celebration.whatNow", { defaultValue: "מה עכשיו?" })}
        </Typography>

        <Stack spacing={1} sx={{ width: "100%" }} direction={isMobile ? "column" : "row"}>
          {phoneDigits && (
            <Button
              fullWidth
              variant="outlined"
              color="success"
              startIcon={<WhatsApp />}
              href={`https://wa.me/${phoneDigits}`}
              target="_blank"
              rel="noopener"
            >
              {translate("contactsPage.newContact.celebration.whatsapp", { defaultValue: "וואטסאפ ברכה" })}
            </Button>
          )}
          <Button fullWidth variant="outlined" startIcon={<AutoAwesome />} onClick={onClose}>
            {translate("contactsPage.newContact.celebration.findMatches", { defaultValue: "חיפוש התאמות" })}
          </Button>
        </Stack>

        <Stack spacing={1} sx={{ width: "100%" }} direction={isMobile ? "column" : "row"}>
          <Button fullWidth variant="text" startIcon={<AddIcon />} onClick={onAddAnother}>
            {translate("contactsPage.newContact.celebration.addAnother", { defaultValue: "הוספת לקוח נוסף" })}
          </Button>
          <Button fullWidth variant="contained" color="primary" onClick={onClose}>
            {translate("contactsPage.newContact.celebration.done", { defaultValue: "סיום" })}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}
